import {
  Button,
  Caption,
  Grid,
  List,
  Modal,
  useToastAlert,
} from '@design-systems/canvas-ui'
import Icon, { ArrowsSwapIcon } from '@design-systems/react-icons'
import { useAuth } from '@praxis/component-auth'
import { useEnv } from '@praxis/component-runtime-env'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { envConfigs, ImpersonationProfile, ReactAppEnv } from 'apiConfig'
import UserSearchAutocomplete, {
  UserOption,
} from 'users/components/UserSearchAutocomplete'
import { UserContext } from '../UserContext'
import {
  applyImpersonatedUser,
  getImpersonationErrorMessage,
  impersonateUser,
} from './impersonationApi'
import {
  getRecentImpersonatedUsers,
  RecentImpersonatedUser,
  saveRecentImpersonatedUser,
} from './impersonationStorage'
import { IsolatedComponent } from '@plm-core/utils'

type Props = {
  isOpen: boolean
  onClose: () => void
}

const toRecentUser = (
  user: UserOption | ImpersonationProfile,
): RecentImpersonatedUser => {
  if ('data' in user) {
    return {
      userProfileId: user.id,
      firstName: user.data.firstName ?? '',
      lastName: user.data.lastName ?? '',
      companyName: user.data.companyName ?? '',
      email: user.data.email ?? '',
    }
  }
  return {
    userProfileId: user.userProfileId,
    firstName: user.firstName,
    lastName: user.lastName,
    companyName: user.companyName,
    email: user.email,
  }
}

const getProfileLabel = (profile: RecentImpersonatedUser) =>
  `${profile.firstName} ${profile.lastName} - ${profile.companyName || 'Target'} - ${profile.email}`

const ImpersonationModal = ({ isOpen, onClose }: Props) => {
  const { session } = useAuth()
  const { environment } = useEnv() as { environment: ReactAppEnv }
  const { setSparkUser } = useContext(UserContext)
  const makeToast = useToastAlert()
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null)
  const [recentUsers, setRecentUsers] = useState(getRecentImpersonatedUsers)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const profiles = useMemo(
    () => envConfigs[environment]?.impersonationProfiles ?? [],
    [environment],
  )

  useEffect(() => {
    if (isOpen) {
      setSelectedUser(null)
      setRecentUsers(getRecentImpersonatedUsers())
    }
  }, [isOpen])

  const handleImpersonate = useCallback(
    async (user: RecentImpersonatedUser) => {
      if (!session?.accessToken || isSubmitting) return
      setIsSubmitting(true)
      try {
        const impersonatedUser = await impersonateUser({
          userProfileId: user.userProfileId,
          accessToken: session.accessToken,
          email: session.userInfo?.email ?? '',
        })
        impersonatedUser.accessToken = session.accessToken
        setRecentUsers(saveRecentImpersonatedUser(user))
        onClose()
        applyImpersonatedUser(impersonatedUser, setSparkUser)
      } catch (error) {
        console.error('Unable to impersonate user', error)
        makeToast({
          variant: 'error',
          heading: 'Unable to impersonate user',
          message: getImpersonationErrorMessage(error),
        })
      } finally {
        setIsSubmitting(false)
      }
    },
    [isSubmitting, makeToast, onClose, session, setSparkUser],
  )

  const handleConfirm = () => {
    if (selectedUser) void handleImpersonate(toRecentUser(selectedUser))
  }

  return (
    <Modal
      headingText="Impersonation"
      isOpen={isOpen}
      onClose={onClose}
      size="md"
    >
      <Grid.Container direction="column">
        <Grid.Item>
          <Grid.Container align="center">
            <Grid.Item size={{ xs: true }}>
              <IsolatedComponent>
                <UserSearchAutocomplete
                  scope={{ kind: 'all' }}
                  value={selectedUser}
                  onUserSelect={setSelectedUser}
                  label="Search users"
                  placeholder="Search name, email, or profile ID"
                />
              </IsolatedComponent>
            </Grid.Item>
            <Grid.Item className="hc8-mt-6x">
              <Button
                variant="primary"
                onClick={handleConfirm}
                isDisabled={!selectedUser || isSubmitting}
              >
                {isSubmitting ? (
                  'Impersonating…'
                ) : (
                  <>
                    Impersonate <Icon svg={ArrowsSwapIcon} />
                  </>
                )}
              </Button>
            </Grid.Item>
          </Grid.Container>
        </Grid.Item>
        {recentUsers.length > 0 && (
          <Grid.Item>
            <strong>Recently impersonated</strong>
            <List>
              {recentUsers.map((user) => (
                <List.Item
                  key={user.userProfileId}
                  isSeparated
                  onClick={() => void handleImpersonate(user)}
                  aria-disabled={isSubmitting}
                >
                  <Grid.Container align="center" spacing="dense">
                    <Grid.Item size={{ xs: true }}>
                      <Caption>{getProfileLabel(user)}</Caption>
                    </Grid.Item>
                    <Grid.Item>
                      <Icon svg={ArrowsSwapIcon} />
                    </Grid.Item>
                  </Grid.Container>
                </List.Item>
              ))}
            </List>
          </Grid.Item>
        )}
        {profiles.length > 0 && (
          <Grid.Item>
            <strong>
              Quick users for {environment === 'local' ? 'dev' : environment}
            </strong>
            <List>
              {profiles.map((profile, index) => {
                const user = toRecentUser(profile)
                const profileText = profile.label
                  ? `${profile.label} — ${getProfileLabel(user)}`
                  : getProfileLabel(user)
                return (
                  <List.Item
                    key={`${profile.userProfileId}-${profile.label ?? index}`}
                    isSeparated
                    onClick={() => void handleImpersonate(user)}
                    aria-disabled={isSubmitting}
                  >
                    <Grid.Container align="center" spacing="dense">
                      <Grid.Item size={{ xs: true }}>
                        <Caption>{profileText}</Caption>
                      </Grid.Item>
                      <Grid.Item>
                        <Icon svg={ArrowsSwapIcon} />
                      </Grid.Item>
                    </Grid.Container>
                  </List.Item>
                )
              })}
            </List>
          </Grid.Item>
        )}
      </Grid.Container>
    </Modal>
  )
}

export default ImpersonationModal
